<script lang="ts">
  import { link } from "svelte-spa-router";
  import { adminKey, isSignedIn } from "../lib/adminStore";
  import { authCheck } from "../lib/api";
  import { entries, hasStub } from "../lib/content";

  let pending = "";
  let signingIn = false;
  let error: string | null = null;

  async function signIn() {
    if (!pending) return;
    signingIn = true;
    error = null;
    try {
      const ok = await authCheck(pending);
      if (!ok) {
        error = "That key didn't match. Try again.";
      } else {
        adminKey.set(pending);
        pending = "";
      }
    } catch (e) {
      error = (e as Error).message;
    } finally {
      signingIn = false;
    }
  }

  function signOut() {
    adminKey.set(null);
  }

  // Group SOPs by source: KV-backed (live edits or new) vs static stub.
  // Useful in the admin dashboard so the user knows what's a code-bound stub
  // vs. a live override they can revert.
  $: kvBacked = $entries.filter((s) => !hasStub(s.slug)).slice().sort((a, b) => a.title.localeCompare(b.title));
  $: stubBacked = $entries.filter((s) => hasStub(s.slug)).slice().sort((a, b) => a.title.localeCompare(b.title));
</script>

<div class="flex flex-col gap-6">
  <div class="flex flex-col gap-2">
    <h1 class="text-3xl font-semibold tracking-tight">Admin</h1>
    <p class="text-text-muted">
      {#if $isSignedIn}
        Edit any SOP, create a new one, or sign out.
      {:else}
        Enter the admin key to edit content.
      {/if}
    </p>
  </div>

  {#if !$isSignedIn}
    <form class="panel p-5 flex flex-col gap-3 max-w-md" on:submit|preventDefault={signIn}>
      <label class="flex flex-col gap-1.5">
        <span class="text-xs uppercase tracking-wider text-text-dim">Admin key</span>
        <input
          bind:value={pending}
          type="password"
          autocomplete="off"
          class="bg-bg-raised border border-border rounded-md px-3 py-2 text-sm text-text outline-none focus:border-accent"
          placeholder="paste the shared admin key"
        />
      </label>
      {#if error}
        <div class="text-sm text-danger">{error}</div>
      {/if}
      <button type="submit" class="btn-primary self-start" disabled={signingIn || !pending}>
        {signingIn ? "Checking..." : "Sign in"}
      </button>
      <p class="text-xs text-text-dim mt-2">
        The key is stored in this browser's localStorage. It does not transit anywhere
        except as the <code>X-Admin-Key</code> header on edit API calls.
      </p>
    </form>
  {:else}
    <div class="flex flex-wrap gap-3">
      <a use:link href="/admin/new" class="btn-primary">+ New SOP</a>
      <button type="button" class="btn" on:click={signOut}>Sign out</button>
    </div>

    {#if kvBacked.length > 0}
      <section class="flex flex-col gap-3">
        <h2 class="text-sm uppercase tracking-wider text-text-dim">Live overrides ({kvBacked.length})</h2>
        <p class="text-xs text-text-muted">SOPs created or edited via this UI. Stored in Cloudflare KV.</p>
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {#each kvBacked as s (s.slug)}
            <a
              use:link
              href={`/admin/edit/${s.slug}`}
              class="panel p-4 hover:border-border-strong transition-colors flex items-center justify-between gap-3"
            >
              <div class="min-w-0">
                <div class="text-sm font-medium text-text truncate">{s.title}</div>
                <div class="text-xs text-text-dim truncate">/{s.slug}</div>
              </div>
              <span class="pill text-text-muted text-[10px]">edit ›</span>
            </a>
          {/each}
        </div>
      </section>
    {/if}

    <section class="flex flex-col gap-3">
      <h2 class="text-sm uppercase tracking-wider text-text-dim">Static stubs ({stubBacked.length})</h2>
      <p class="text-xs text-text-muted">
        Shipped with the app. Editing one creates a live override that wins until deleted.
      </p>
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {#each stubBacked as s (s.slug)}
          <a
            use:link
            href={`/admin/edit/${s.slug}`}
            class="panel p-4 hover:border-border-strong transition-colors flex items-center justify-between gap-3"
          >
            <div class="min-w-0">
              <div class="text-sm font-medium text-text truncate">{s.title}</div>
              <div class="text-xs text-text-dim truncate">/{s.slug}</div>
            </div>
            <span class="pill text-text-muted text-[10px]">edit ›</span>
          </a>
        {/each}
      </div>
    </section>
  {/if}
</div>
